import React, { createContext, useContext, useEffect, useState } from 'react';
import { applyAuthorizedClientType, captureSessionFromUrl, clearSession, setAuthToken, setCompany } from '../../tools/session/clientSession';
import { clearClientDataCache } from '../../services/clientDataService';
import { useLoading } from '../../common/loading/LoadingProvider';
import { REQUIRE_AUTH } from '../../tools/env';
import { clearCurrentUserCache, fetchCurrentUser } from './APIs/authApi';
import AuthRequiredView from './AuthRequiredView';
import './AuthRequiredView.css';

const AuthContext = createContext(null);

let globalInitialized = false;
let globalAuthState = null;

export function resetAuthGlobals() {
  globalInitialized = false;
  globalAuthState = null;
  clearCurrentUserCache();
}

export function AuthProvider({ children }) {
  const { showLoader, hideLoader } = useLoading();
  const [user, setUser] = useState(globalAuthState?.user || null);
  const [token, setToken] = useState(globalAuthState?.token || null);
  const [isLoading, setIsLoading] = useState(!globalInitialized);
  const [isAuthenticated, setIsAuthenticated] = useState(globalAuthState?.isAuthenticated || false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (globalInitialized) return undefined;
    const initializeAuth = async () => {
      globalInitialized = true;
      setIsLoading(true);
      showLoader('VERIFICA AUTENTICAZIONE...', 1, { showPercentage: false });
      setError(null);
      const { token: authToken } = captureSessionFromUrl();
      setToken(authToken);
      if (authToken) {
        try {
          const userData = await fetchCurrentUser();
          if (userData?.company) setCompany(userData.company);
          applyAuthorizedClientType(userData);
          setUser(userData);
          setIsAuthenticated(true);
          globalAuthState = { user: userData, token: authToken, isAuthenticated: true };
        } catch (err) {
          console.error('Error verifying auth token:', err);
          setToken(null);
          setAuthToken(null);
          setIsAuthenticated(false);
          setError(err.status === 401 ? 'Token non valido o scaduto' : 'Errore di connessione durante la verifica');
          globalAuthState = null;
        }
      } else {
        setIsAuthenticated(false);
        globalAuthState = null;
      }
      setIsLoading(false);
      hideLoader();
    };
    initializeAuth();
    return undefined;
  }, [hideLoader, showLoader]);

  const login = async (newToken) => {
    if (!newToken) return false;
    setAuthToken(newToken);
    setToken(newToken);
    try {
      const userData = await fetchCurrentUser();
      if (userData?.company) setCompany(userData.company);
      applyAuthorizedClientType(userData);
      setUser(userData);
      setIsAuthenticated(true);
      setError(null);
      return true;
    } catch (err) {
      setAuthToken(null);
      setToken(null);
      setIsAuthenticated(false);
      setError('Token non valido');
      return false;
    }
  };

  const logout = async () => {
    await clearSession({ clearCaches: clearClientDataCache });
    clearCurrentUserCache();
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    globalAuthState = null;
  };

  const refreshUser = async () => {
    if (!token) return false;
    try {
      const userData = await fetchCurrentUser();
      if (userData?.company) setCompany(userData.company);
      applyAuthorizedClientType(userData);
      setUser(userData);
      setIsAuthenticated(true);
      return true;
    } catch (err) {
      await logout();
      return false;
    }
  };

  const allowedServices = user?.all_in_one_services || [];
  const allowedPermissions = user?.permissions || [];
  const hasService = (serviceKey) => allowedServices.includes(serviceKey) || allowedPermissions.includes(serviceKey);

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated,
    error,
    login,
    logout,
    refreshUser,
    allowedServices,
    allowedPermissions,
    hasService,
  };

  if (isLoading) return null;
  if (REQUIRE_AUTH && !isAuthenticated) {
    return <AuthRequiredView error={error} />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
